import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMedplum } from '@medplum/react';
import {
  DiagnosticReport,
  Patient,
  ServiceRequest,
  Specimen,
  Practitioner,
} from '@medplum/fhirtypes';
import { notifications } from '@mantine/notifications';
import { TiptapJSON, SignerEntry } from '../types/template.types';
import {
  createReportSaveService,
  SaveReportOptions,
} from '../services/report-save.service';
import { createDiagnosticReportReviewTask } from '../../utils/fhir-helpers';

export interface UseReportSaveProps {
  serviceRequest?: ServiceRequest | null;
  patient?: Patient;
  specimens?: Specimen[];
  templateId: string;
  templateVersion: string;
  onSaveSuccess?: (report: DiagnosticReport) => void;
  onSaveError?: (error: Error) => void;
}

export interface UseReportSaveReturn {
  /** Save report as draft */
  saveDraft: (
    content: TiptapJSON,
    existingReportId?: string,
    options?: Partial<SaveReportOptions>
  ) => Promise<DiagnosticReport | null>;
  /** Save and submit for review */
  submitForReview: (
    content: TiptapJSON,
    reviewerIds: string[],
    options?: Partial<SaveReportOptions>
  ) => Promise<DiagnosticReport | null>;
  /** Update existing report */
  updateReport: (
    reportId: string,
    content: TiptapJSON,
    options?: Partial<SaveReportOptions>
  ) => Promise<DiagnosticReport | null>;
  /** Load existing report content */
  loadReportContent: (reportId: string) => Promise<{
    content: TiptapJSON;
    templateId: string;
    signers?: SignerEntry[];
  } | null>;
  /** Check if report has Tiptap content */
  hasTiptapContent: (report: DiagnosticReport) => boolean;
  /** Saving state */
  saving: boolean;
  /** Loading state */
  loading: boolean;
  /** Error message */
  error: string | null;
}

/**
 * Hook for saving and loading report content
 * @param root0 - Hook configuration options
 * @param root0.serviceRequest - FHIR ServiceRequest resource
 * @param root0.patient - FHIR Patient resource
 * @param root0.specimens - Array of FHIR Specimen resources
 * @param root0.templateId - Template ID used for the report
 * @param root0.templateVersion - Template version string
 * @param root0.onSaveSuccess - Callback on successful save
 * @param root0.onSaveError - Callback on save error
 * @returns Report save operations and state
 */
export function useReportSave({
  serviceRequest,
  patient,
  specimens = [],
  templateId,
  templateVersion,
  onSaveSuccess,
  onSaveError,
}: UseReportSaveProps): UseReportSaveReturn {
  const { t } = useTranslation('lims');
  const medplum = useMedplum();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const service = useMemo(() => createReportSaveService(medplum), [medplum]);

  /**
   * Save report as draft (registered status)
   */
  const saveDraft = useCallback(
    async (
      content: TiptapJSON,
      existingReportId?: string,
      options?: Partial<SaveReportOptions>
    ): Promise<DiagnosticReport | null> => {
      if (!serviceRequest || !patient) {
        setError(t('reportEditor.save.missingOrderData'));
        return null;
      }

      setSaving(true);
      setError(null);

      try {
        const report = await service.saveReport(
          serviceRequest,
          patient,
          specimens,
          {
            content,
            templateId,
            templateVersion,
            status: 'registered',
            ...options,
          },
          existingReportId
        );

        notifications.show({
          title: t('reportEditor.general.success'),
          message: t('reportEditor.notifications.saveSuccess'),
          color: 'green',
        });

        onSaveSuccess?.(report);
        return report;
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : t('reportEditor.save.saveErrorDefault');
        setError(message);
        notifications.show({
          title: t('reportEditor.error'),
          message,
          color: 'red',
        });
        onSaveError?.(err instanceof Error ? err : new Error(message));
        return null;
      } finally {
        setSaving(false);
      }
    },
    [
      t,
      serviceRequest,
      patient,
      specimens,
      templateId,
      templateVersion,
      service,
      onSaveSuccess,
      onSaveError,
    ]
  );

  /**
   * Save and submit for review (preliminary status)
   * Creates review tasks for selected reviewers
   */
  const submitForReview = useCallback(
    async (
      content: TiptapJSON,
      reviewerIds: string[],
      options?: Partial<SaveReportOptions>
    ): Promise<DiagnosticReport | null> => {
      if (!serviceRequest || !patient) {
        setError(t('reportEditor.save.missingOrderData'));
        return null;
      }

      // Validate reviewers selection
      if (reviewerIds.length === 0) {
        const msg = t('reportEditor.save.reviewerRequired');
        setError(msg);
        notifications.show({
          title: t('reportEditor.error'),
          message: msg,
          color: 'red',
        });
        return null;
      }

      setSaving(true);
      setError(null);

      try {
        // 1. Save the report
        const report = await service.saveReport(
          serviceRequest,
          patient,
          specimens,
          {
            content,
            templateId,
            templateVersion,
            status: 'preliminary',
            ...options,
          }
        );

        // 2. Create review tasks for each selected reviewer
        if (report?.id) {
          try {
            // Load all practitioners to get reviewer details
            const practitioners = await medplum.searchResources(
              'Practitioner',
              {
                _sort: 'name',
              }
            );

            const currentUser = medplum.getProfile();

            for (const reviewerId of reviewerIds) {
              const reviewer =
                practitioners.find((p) => p.id === reviewerId) ||
                (reviewerId === currentUser?.id
                  ? (currentUser as Practitioner)
                  : undefined);

              if (reviewer) {
                const reviewTask = createDiagnosticReportReviewTask(
                  report,
                  serviceRequest,
                  patient,
                  reviewer
                );

                await medplum.createResource(reviewTask);
              }
            }

            notifications.show({
              title: t('reportEditor.general.success'),
              message: t('reportEditor.save.submittedToReviewers', {
                count: reviewerIds.length,
              }),
              color: 'green',
            });
          } catch (taskError) {
            console.error('Error creating review tasks:', taskError);
            notifications.show({
              title: t('reportEditor.general.attention'),
              message: t('reportEditor.save.reviewTasksWarning'),
              color: 'yellow',
            });
          }
        } else {
          notifications.show({
            title: t('reportEditor.general.success'),
            message: t('reportEditor.save.submittedToReview'),
            color: 'green',
          });
        }

        onSaveSuccess?.(report);
        return report;
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : t('reportEditor.save.submitErrorDefault');
        setError(message);
        notifications.show({
          title: t('reportEditor.error'),
          message,
          color: 'red',
        });
        onSaveError?.(err instanceof Error ? err : new Error(message));
        return null;
      } finally {
        setSaving(false);
      }
    },
    [
      t,
      serviceRequest,
      patient,
      specimens,
      templateId,
      templateVersion,
      service,
      medplum,
      onSaveSuccess,
      onSaveError,
    ]
  );

  /**
   * Update existing report
   */
  const updateReport = useCallback(
    async (
      reportId: string,
      content: TiptapJSON,
      options?: Partial<SaveReportOptions>
    ): Promise<DiagnosticReport | null> => {
      if (!serviceRequest || !patient) {
        setError(t('reportEditor.save.missingOrderData'));
        return null;
      }

      setSaving(true);
      setError(null);

      try {
        const report = await service.saveReport(
          serviceRequest,
          patient,
          specimens,
          {
            content,
            templateId,
            templateVersion,
            status: 'registered',
            ...options,
          },
          reportId
        );

        notifications.show({
          title: t('reportEditor.general.success'),
          message: t('reportEditor.save.reportUpdated'),
          color: 'green',
        });

        onSaveSuccess?.(report);
        return report;
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : t('reportEditor.save.updateError');
        setError(message);
        notifications.show({
          title: t('reportEditor.error'),
          message,
          color: 'red',
        });
        onSaveError?.(err instanceof Error ? err : new Error(message));
        return null;
      } finally {
        setSaving(false);
      }
    },
    [
      t,
      serviceRequest,
      patient,
      specimens,
      templateId,
      templateVersion,
      service,
      onSaveSuccess,
      onSaveError,
    ]
  );

  /**
   * Load existing report content
   */
  const loadReportContent = useCallback(
    async (reportId: string) => {
      setLoading(true);
      setError(null);

      try {
        return await service.loadReportContent(reportId);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : t('reportEditor.save.loadError');
        setError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [service, t]
  );

  /**
   * Check if report has Tiptap content
   */
  const hasTiptapContent = useCallback(
    (report: DiagnosticReport) => {
      return service.hasTiptapContent(report);
    },
    [service]
  );

  return {
    saveDraft,
    submitForReview,
    updateReport,
    loadReportContent,
    hasTiptapContent,
    saving,
    loading,
    error,
  };
}
